import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";
import {
  hasConflictingWorkroomKeys,
  normalizeWorkroomInputReference,
  normalizeWorkroomReference,
} from "./workrooms.ts";

type WorkroomStruct = Schema.Top & {
  readonly fields: Schema.Struct.Fields & { readonly workroomKey: Schema.Top };
};

/** Old persisted events/snapshots decode to the canonical shape; encoding
 * always emits Workroom names. The ledger's original bytes stay unchanged. */
export function withWorkroomReadCompatibility<S extends WorkroomStruct>(schema: S) {
  return workroomCompatibility(schema, false);
}

/** New commands admit old key aliases, but never contradictory launch intent. */
export function withWorkroomInputCompatibility<S extends WorkroomStruct>(schema: S) {
  return workroomCompatibility(schema, true);
}

function workroomCompatibility<S extends WorkroomStruct>(schema: S, commandInput: boolean) {
  // Keep the original encoded field structure so framework JSON codecs retain
  // each field's optional/null/nested encoding. An Unknown source makes the
  // whole record an opaque JSON value and rejects ordinary optional undefined
  // fields produced by the thread projector.
  const fields: Schema.Struct.Fields = Object.fromEntries(
    Object.entries(schema.fields).map(([key, field]) => [key, Schema.toEncoded(field)]),
  );
  const source = Schema.Struct({
    ...fields,
    workroomKey: Schema.optional(Schema.toEncoded(schema.fields.workroomKey)),
    // Read alias only. The canonical target removes it before all new encoding.
    skillRoomKey: Schema.optional(Schema.toEncoded(schema.fields.workroomKey)),
    ...(schema.fields.kind
      ? { kind: Schema.Union([Schema.toEncoded(schema.fields.kind), Schema.Literal("skill")]) }
      : {}),
  });
  const admittedSource = commandInput
    ? source.check(
        Schema.makeFilter(
          (value) => !hasConflictingWorkroomKeys(value) || "conflicting Workroom references",
        ),
      )
    : source;
  return admittedSource.pipe(
    Schema.decodeTo(
      schema,
      SchemaTransformation.transform<S["Encoded"], typeof source.Type>({
        decode: (value) =>
          (commandInput
            ? normalizeWorkroomInputReference(value)
            : normalizeWorkroomReference(value)) as S["Encoded"],
        encode: (value) => value as typeof source.Type,
      }),
    ),
  ) as Schema.Codec<S["Type"], S["Encoded"], S["DecodingServices"], S["EncodingServices"]>;
}
